// Imperative card → shelf-slot star animation. Spawns a fixed-position
// transient `.fly-star` node, animates it via the Web Animations API,
// and removes it on finish. Honours prefers-reduced-motion by short-
// circuiting (onArrive fires synchronously, no DOM node spawned).

export interface FlyStarOptions {
  from: HTMLElement;
  to: HTMLElement;
  emoji: string;
  durationMs?: number;
  onArrive?: () => void;
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  } catch {
    return false;
  }
}

export function flyStar({ from, to, emoji, durationMs = 550, onArrive }: FlyStarOptions): void {
  if (prefersReducedMotion()) {
    onArrive?.();
    return;
  }

  const fromRect = from.getBoundingClientRect();
  const toRect = to.getBoundingClientRect();
  if (fromRect.width === 0 && fromRect.height === 0) return;

  const node = document.createElement('div');
  node.className = 'fly-star';
  node.textContent = emoji;
  node.style.left = `${fromRect.left + fromRect.width / 2}px`;
  node.style.top = `${fromRect.top + fromRect.height / 2}px`;
  node.style.transform = 'translate(-50%, -50%) scale(1.6)';
  document.body.appendChild(node);

  const dx = toRect.left + toRect.width / 2 - (fromRect.left + fromRect.width / 2);
  const dy = toRect.top + toRect.height / 2 - (fromRect.top + fromRect.height / 2);

  const animation = node.animate(
    [
      { transform: 'translate(-50%, -50%) scale(1.6)', opacity: 1 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.4)`, opacity: 0 },
    ],
    {
      duration: durationMs,
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      fill: 'forwards',
    },
  );

  const cleanup = () => {
    onArrive?.();
    if (node.isConnected) node.remove();
  };

  if (animation && typeof animation.finished?.then === 'function') {
    animation.finished.then(cleanup, cleanup);
  } else if (animation) {
    animation.onfinish = cleanup;
  } else {
    // No Web Animations API support — bail and call onArrive immediately.
    cleanup();
  }
}
